import { useState, useCallback } from 'react';
import { useApi } from '@/hooks/useApi';
import { apiFetch } from '@/lib/api';
import {
  AlertTriangle,
  Plus,
  ShieldAlert,
  CheckCircle,
  DollarSign,
  Loader2,
  X,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────────────────

type IncidentType = 'accident' | 'collision' | 'theft' | 'vandalism' | 'breakdown' | 'weather' | 'other';
type Severity = 'minor' | 'moderate' | 'major' | 'critical';
type IncidentStatus = 'reported' | 'investigating' | 'resolved' | 'closed';

interface Incident {
  id: string;
  incident_number: string;
  vehicle_id: string;
  vehicle_name: string;
  driver_id?: string;
  driver_name?: string;
  incident_type: IncidentType;
  severity: Severity;
  status: IncidentStatus;
  date: string;
  location?: string;
  description: string;
  damage_assessment?: string;
  police_report_number?: string;
  insurance_claim_number?: string;
  estimated_cost?: number;
  actual_cost?: number;
  created_at: string;
}

interface IncidentsResponse {
  data: Incident[];
  total: number;
  metrics: {
    total: number;
    open: number;
    resolved: number;
    total_cost: number;
  };
}

interface VehicleOption { id: string; name: string }
interface DriverOption { id: string; name: string }

const SEVERITY_STYLES: Record<Severity, { bg: string; text: string; border: string }> = {
  minor:    { bg: 'rgba(156,163,175,0.15)', text: '#9CA3AF', border: 'rgba(156,163,175,0.3)' },
  moderate: { bg: 'rgba(245,158,11,0.15)',  text: '#F59E0B', border: 'rgba(245,158,11,0.3)' },
  major:    { bg: 'rgba(249,115,22,0.15)',  text: '#F97316', border: 'rgba(249,115,22,0.3)' },
  critical: { bg: 'rgba(239,68,68,0.15)',   text: '#EF4444', border: 'rgba(239,68,68,0.3)' },
};

const STATUS_STYLES: Record<IncidentStatus, { bg: string; text: string; border: string }> = {
  reported:      { bg: 'rgba(239,68,68,0.15)',  text: '#EF4444', border: 'rgba(239,68,68,0.3)' },
  investigating: { bg: 'rgba(245,158,11,0.15)', text: '#F59E0B', border: 'rgba(245,158,11,0.3)' },
  resolved:      { bg: 'rgba(16,185,129,0.15)', text: '#10B981', border: 'rgba(16,185,129,0.3)' },
  closed:        { bg: 'rgba(107,114,128,0.15)', text: '#6B7280', border: 'rgba(107,114,128,0.3)' },
};

export default function IncidentsPage() {
  const { data, isLoading, refetch } = useApi<IncidentsResponse>('/api/incidents');
  const { data: vehiclesData } = useApi<{ vehicles: VehicleOption[] }>('/api/vehicles?pageSize=100');
  const { data: driversData } = useApi<{ drivers: DriverOption[] }>('/api/drivers');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const incidents = data?.data ?? [];
  const metrics = data?.metrics ?? { total: 0, open: 0, resolved: 0, total_cost: 0 };
  const vehicles = vehiclesData?.vehicles ?? [];
  const drivers = driversData?.drivers ?? [];

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    const fd = new FormData(e.currentTarget);
    const selectedVehicle = vehicles.find(v => v.id === fd.get('vehicle_id'));
    const selectedDriver = drivers.find(d => d.id === fd.get('driver_id'));
    try {
      await apiFetch('/api/incidents', {
        method: 'POST',
        body: JSON.stringify({
          vehicle_id: fd.get('vehicle_id'),
          vehicle_name: selectedVehicle?.name ?? '',
          driver_id: fd.get('driver_id') || undefined,
          driver_name: selectedDriver?.name ?? undefined,
          incident_type: fd.get('incident_type'),
          severity: fd.get('severity'),
          date: fd.get('date'),
          location: fd.get('location') || undefined,
          description: fd.get('description'),
          damage_assessment: fd.get('damage_assessment') || undefined,
          police_report_number: fd.get('police_report_number') || undefined,
          insurance_claim_number: fd.get('insurance_claim_number') || undefined,
          estimated_cost: fd.get('estimated_cost') ? Number(fd.get('estimated_cost')) : undefined,
        }),
      });
      setShowForm(false);
      refetch();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create incident');
    } finally {
      setSaving(false);
    }
  }, [vehicles, drivers, refetch]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: 'rgba(239,68,68,0.1)' }}>
            <AlertTriangle className="w-6 h-6" style={{ color: '#EF4444' }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>Incidents & Accidents</h1>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Track and manage fleet incidents</p>
          </div>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground rounded-lg transition-colors"
          style={{ background: '#EF4444' }}
        >
          <Plus className="w-4 h-4" />
          Report Incident
        </button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={AlertTriangle} label="Total Incidents" value={String(metrics.total)} color="#EF4444" />
        <StatCard icon={ShieldAlert} label="Open" value={String(metrics.open)} color="#F59E0B" />
        <StatCard icon={CheckCircle} label="Resolved" value={String(metrics.resolved)} color="#10B981" />
        <StatCard icon={DollarSign} label="Total Cost" value={`$${metrics.total_cost.toLocaleString()}`} color="#A855F7" />
      </div>

      {/* Table */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(var(--glass-blur))' }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--muted-foreground)' }} />
          </div>
        ) : incidents.length === 0 ? (
          <div className="text-center py-16" style={{ color: 'var(--muted-foreground)' }}>
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>No incidents recorded</p>
            <p className="text-xs mt-1">Click "Report Incident" to log a new incident.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--glass-border)' }}>
                  {['Incident #', 'Date', 'Vehicle', 'Driver', 'Type', 'Severity', 'Status', 'Cost'].map(h => (
                    <th key={h} className="text-left text-xs font-medium uppercase tracking-wide px-4 py-3" style={{ color: 'var(--muted-foreground)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {incidents.map((inc) => {
                  const sev = SEVERITY_STYLES[inc.severity] ?? SEVERITY_STYLES.minor;
                  const st = STATUS_STYLES[inc.status] ?? STATUS_STYLES.reported;
                  return (
                    <tr key={inc.id} className="border-b transition-colors hover:bg-white/5" style={{ borderColor: 'var(--glass-border)' }}>
                      <td className="px-4 py-3"><span className="text-sm font-mono font-medium" style={{ color: '#EF4444' }}>{inc.incident_number}</span></td>
                      <td className="px-4 py-3"><span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{new Date(inc.date).toLocaleDateString()}</span></td>
                      <td className="px-4 py-3"><span className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{inc.vehicle_name}</span></td>
                      <td className="px-4 py-3"><span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{inc.driver_name ?? '--'}</span></td>
                      <td className="px-4 py-3"><span className="text-sm capitalize" style={{ color: 'var(--foreground)' }}>{inc.incident_type.replace('_', ' ')}</span></td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border capitalize"
                          style={{ background: sev.bg, color: sev.text, borderColor: sev.border }}>{inc.severity}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border capitalize"
                          style={{ background: st.bg, color: st.text, borderColor: st.border }}>{inc.status}</span>
                      </td>
                      <td className="px-4 py-3"><span className="text-sm font-mono" style={{ color: 'var(--foreground)' }}>
                        {inc.actual_cost != null ? `$${inc.actual_cost.toLocaleString()}` : inc.estimated_cost != null ? `~$${inc.estimated_cost.toLocaleString()}` : '--'}
                      </span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div className="w-full max-w-lg rounded-xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ background: 'var(--surface-1)', border: '1px solid var(--border-subtle)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>Report Incident</h2>
              <button onClick={() => setShowForm(false)} className="p-1 rounded hover:bg-white/10"><X className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} /></button>
            </div>
            {formError && <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444' }}>{formError}</div>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <FormField label="Vehicle" required>
                <select name="vehicle_id" required className="form-select">
                  <option value="">Select vehicle...</option>
                  {vehicles.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </FormField>
              <FormField label="Driver">
                <select name="driver_id" className="form-select">
                  <option value="">Select driver (optional)...</option>
                  {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Incident Type" required>
                  <select name="incident_type" required className="form-select">
                    {(['accident', 'collision', 'theft', 'vandalism', 'breakdown', 'weather', 'other'] as IncidentType[]).map(t =>
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    )}
                  </select>
                </FormField>
                <FormField label="Severity" required>
                  <select name="severity" required className="form-select">
                    {(['minor', 'moderate', 'major', 'critical'] as Severity[]).map(s =>
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    )}
                  </select>
                </FormField>
              </div>
              <FormField label="Date" required>
                <input type="date" name="date" required className="form-input" defaultValue={new Date().toISOString().slice(0, 10)} />
              </FormField>
              <FormField label="Location">
                <input type="text" name="location" className="form-input" placeholder="Address or description" />
              </FormField>
              <FormField label="Description" required>
                <textarea name="description" required rows={3} className="form-input" placeholder="Describe what happened..." />
              </FormField>
              <FormField label="Damage Assessment">
                <textarea name="damage_assessment" rows={2} className="form-input" placeholder="Describe the damage..." />
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Police Report #">
                  <input type="text" name="police_report_number" className="form-input" />
                </FormField>
                <FormField label="Insurance Claim #">
                  <input type="text" name="insurance_claim_number" className="form-input" />
                </FormField>
              </div>
              <FormField label="Estimated Cost ($)">
                <input type="number" name="estimated_cost" step="0.01" min="0" className="form-input" />
              </FormField>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm rounded-lg" style={{ color: 'var(--muted-foreground)' }}>Cancel</button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-foreground rounded-lg flex items-center gap-2"
                  style={{ background: '#EF4444', opacity: saving ? 0.7 : 1 }}>
                  {saving && <Loader2 className="w-3 h-3 animate-spin" />}
                  Report Incident
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }: {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  label: string; value: string; color: string;
}) {
  return (
    <div className="rounded-xl p-5" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(var(--glass-blur))' }}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>{label}</span>
      </div>
      <span className="text-2xl font-bold" style={{ color }}>{value}</span>
    </div>
  );
}

function FormField({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--foreground)' }}>
        {label}{required && <span style={{ color: '#EF4444' }}> *</span>}
      </label>
      {children}
    </div>
  );
}
